/**
 * Indicador de "novo" no menu Novidades: compara a novidade mais recente com a
 * última vista neste navegador (localStorage — conveniência por aparelho).
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { NovidadePublica } from '@patrociconnect/shared';
import { api } from './api';

const SEEN_KEY = 'pc:novidades:vistaAte';
const SEEN_EVENT = 'pc:novidades:vista';

export interface NovidadesPage {
  items: NovidadePublica[];
  nextCursor: string | null;
}

function readSeenAt(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

export function markNovidadesSeen(latestPublishedAt: string): void {
  try {
    const current = readSeenAt();
    if (!current || latestPublishedAt > current) localStorage.setItem(SEEN_KEY, latestPublishedAt);
  } catch {
    // Sem localStorage o indicador apenas continua aparecendo
  }
  window.dispatchEvent(new Event(SEEN_EVENT));
}

export function useHasUnseenNovidades(): boolean {
  const [seenAt, setSeenAt] = useState<string | null>(() => readSeenAt());

  useEffect(() => {
    const refresh = () => setSeenAt(readSeenAt());
    window.addEventListener(SEEN_EVENT, refresh);
    return () => window.removeEventListener(SEEN_EVENT, refresh);
  }, []);

  const query = useQuery({
    queryKey: ['novidades-latest'],
    staleTime: 30 * 60 * 1000,
    queryFn: () => api.get<NovidadesPage>('/novidades?limit=1', { requireAuth: false }),
  });

  const latest = query.data?.items[0]?.publicadaEm;
  return Boolean(latest && (!seenAt || latest > seenAt));
}
